import { useEffect, useMemo, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { Icon } from '@/components/ui/Icon';
import { NeonButton } from '@/components/ui/NeonButton';
import { Sheet } from '@/components/sheets/Sheet';
import { useReports, type ReportStatus } from '../useReports';
import { useModerationActions } from '../useModerationActions';
import type { ModerationAction } from '@/lib/database.types';
import { cn } from '@/lib/cn';

const FILTER_TABS: { key: ReportStatus; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'reviewing', label: 'Reviewing' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'dismissed', label: 'Dismissed' },
];

const ACTIONS: { key: ModerationAction; label: string; sub: string; destructive?: boolean }[] = [
  { key: 'clear',    label: 'Clear',    sub: 'Restore the user to good standing.' },
  { key: 'warn',     label: 'Warn',     sub: 'Send a warning. No surface change.' },
  { key: 'throttle', label: 'Throttle', sub: 'Rate-limit messages for 24 hours.', destructive: true },
  { key: 'suspend',  label: 'Suspend',  sub: 'Hide from feed for 7 days.', destructive: true },
  { key: 'ban',      label: 'Ban',      sub: 'Permanent removal.', destructive: true },
];

export const AdminQueuePage = () => {
  const [filter, setFilter] = useState<ReportStatus>('open');
  const { reports, loading, error, updateStatus } = useReports(filter);
  const { apply, submitting } = useModerationActions();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ModerationAction | null>(null);
  const [notes, setNotes] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const active = useMemo(
    () => reports.find((r) => r.id === activeId) ?? null,
    [reports, activeId],
  );

  useEffect(() => {
    if (toast) {
      const t = window.setTimeout(() => setToast(null), 2500);
      return () => window.clearTimeout(t);
    }
  }, [toast]);

  const handleConfirmAction = async () => {
    if (!active || !confirmAction) return;
    // Apply sanction
    const result = await apply({
      targetUserId: active.reported_id,
      action: confirmAction,
      notes,
      relatedReport: active.id,
      expiresHours: confirmAction === 'throttle' ? 24 : confirmAction === 'suspend' ? 7 * 24 : undefined,
    });
    if (!result.ok) {
      setToast(result.error ?? 'Action failed.');
      return;
    }
    // Resolve the report
    await updateStatus(active.id, confirmAction === 'clear' ? 'dismissed' : 'resolved');
    setToast(`${confirmAction} applied.`);
    setConfirmAction(null);
    setNotes('');
    setActiveId(null);
  };

  return (
    <AdminShell eyebrow="Moderation · queue">
      <div className="grid grid-cols-1 lg:h-[calc(100dvh-8rem)] lg:grid-cols-[420px_minmax(0,1fr)]">
        {/* LIST */}
        <section className="border-r border-white/[0.06] bg-ink lg:overflow-y-auto">
          <div className="border-b border-white/[0.06] px-5 py-4 md:px-7">
            <SectionMarker className="mb-2">Reports · {filter}</SectionMarker>
            <div className="flex gap-3">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => { setFilter(tab.key); setActiveId(null); }}
                  className={cn(
                    'font-display text-[10px] font-bold uppercase tracking-widest',
                    'transition-colors duration-fast ease-signal',
                    filter === tab.key ? 'text-pink' : 'text-text-muted hover:text-text-primary',
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <p className="px-5 py-6 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
              Loading<span className="text-pink">.</span>
            </p>
          ) : error ? (
            <p className="px-5 py-6 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
              — {error}
            </p>
          ) : reports.length === 0 ? (
            <p className="px-5 py-6 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
              No {filter} reports<span className="text-pink">.</span>
            </p>
          ) : (
            <ul className="divide-y divide-white/[0.06]">
              {reports.map((r) => {
                const isActive = r.id === activeId;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(r.id)}
                      className={cn(
                        'block w-full px-5 py-4 text-left transition-colors duration-fast ease-signal md:px-7',
                        isActive ? 'lg:bg-white/[0.03]' : 'hover:bg-white/[0.02]',
                      )}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="truncate font-display text-sm font-bold uppercase tracking-wide text-text-primary">
                          {r.reportedHandle ? `@${r.reportedHandle}` : r.reported_id.slice(0, 8)}
                        </p>
                        <span className="shrink-0 font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
                          {new Date(r.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-1 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
                        {r.reason.replaceAll('_', ' ')}
                      </p>
                      {r.detail && (
                        <p className="mt-2 line-clamp-2 text-xs text-text-secondary">
                          {r.detail}
                        </p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* DETAIL */}
        <section className="relative hidden bg-ink lg:flex lg:flex-col">
          {!active ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
                Pick a report<span className="text-pink">.</span>
              </p>
            </div>
          ) : (
            <>
              <div className="border-b border-white/[0.06] px-9 py-6">
                <SectionMarker className="mb-2">Report detail</SectionMarker>
                <h2 className="font-display text-2xl font-black uppercase tracking-display">
                  {active.reportedHandle ? `@${active.reportedHandle}` : active.reported_id.slice(0, 8)}
                  <span className="text-pink">.</span>
                </h2>
              </div>
              <div className="flex-1 overflow-y-auto px-9 py-6">
                <dl className="space-y-4 text-sm">
                  <div>
                    <dt className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">Reason</dt>
                    <dd className="mt-1 text-text-primary">{active.reason.replaceAll('_', ' ')}</dd>
                  </div>
                  <div>
                    <dt className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">Reporter ID</dt>
                    <dd className="mt-1 text-text-secondary">{active.reporter_id}</dd>
                  </div>
                  <div>
                    <dt className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">Filed</dt>
                    <dd className="mt-1 text-text-secondary">{new Date(active.created_at).toLocaleString()}</dd>
                  </div>
                  {active.detail && (
                    <div>
                      <dt className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">Detail</dt>
                      <dd className="mt-1 whitespace-pre-wrap text-text-primary">{active.detail}</dd>
                    </div>
                  )}
                </dl>

                <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                  {ACTIONS.map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => setConfirmAction(a.key)}
                      className={cn(
                        'flex flex-col items-start gap-1 border px-4 py-3 text-left',
                        'transition-colors duration-fast ease-signal',
                        a.destructive
                          ? 'border-pink/30 text-pink hover:bg-pink/[0.08]'
                          : 'border-white/[0.10] text-text-primary hover:border-white/[0.30]',
                      )}
                    >
                      <span className="font-display text-[11px] font-bold uppercase tracking-widest">{a.label}</span>
                      <span className="text-[10px] text-text-muted">{a.sub}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <Sheet
        open={confirmAction !== null}
        onClose={() => { setConfirmAction(null); setNotes(''); }}
        eyebrow="Confirm action"
        title={
          <>
            {confirmAction ?? ''}<span className="text-pink">.</span>
          </>
        }
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => { setConfirmAction(null); setNotes(''); }}
              className="font-display text-[11px] font-bold uppercase tracking-widest text-text-muted hover:text-text-primary"
            >
              Cancel
            </button>
            <NeonButton type="button" size="md" disabled={submitting} onClick={handleConfirmAction} trailing={<Icon name="check" size={14} />}>
              {submitting ? 'Applying' : 'Apply'}
            </NeonButton>
          </div>
        }
      >
        <p className="mb-4 text-xs text-text-secondary">
          This is logged in the moderation audit trail. Notes are visible to
          other moderators, not the user.
        </p>
        <label className="block">
          <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
            rows={3}
            placeholder="why this action, with one specific reason."
            className={cn(
              'mt-2 w-full resize-none border border-white/[0.10] bg-charcoal/40 px-3 py-2 text-sm',
              'placeholder:text-text-muted focus:border-pink focus:outline-none',
            )}
          />
        </label>
      </Sheet>

      {toast && (
        <div className="pointer-events-none fixed bottom-8 left-1/2 z-30 -translate-x-1/2 border border-pink/40 bg-ink/95 px-5 py-3 font-display text-[10px] font-bold uppercase tracking-widest text-text-primary shadow-glow-soft backdrop-blur">
          — {toast}
        </div>
      )}
    </AdminShell>
  );
};
